import {
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  Icon,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiArrowLeft, FiBriefcase, FiDollarSign, FiEdit, FiFile, FiFileText, FiUser } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { type NeedDto, type NeedPatchDto } from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { organizationHasSeveralDepartments } from "../../../common/departments/departmentVisibility";
import { useEditor } from "../../../common/editing/save-coordinator";
import { DownloadDocumentIconButton } from "../../../common/documents/DownloadDocumentIconButton/DownloadDocumentIconButton";
import { TipTap, isTiptapContentEmpty } from "../../../common/input/TipTap/TipTap";
import { LabelValueGrid } from "../../sourcing-events/portal/view-single/LabelValueGrid";
import { SectionCard } from "../../sourcing-events/portal/view-single/SectionCard";
import { urls } from "../../../urls";
import { NeedActions } from "./NeedActions";
import { NeedStatusTag } from "./NeedStatusTag";
import { NeedStatusStepper } from "./NeedStatusStepper";
import { useNeedPriorityLabel } from "./useNeedPriorityLabel";

const DASH = "–";
const nokFormatter = new Intl.NumberFormat("nb-NO");

interface Props {
  needId: string;
}

export const NeedViewBody = ({ needId }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useEditor<NeedDto, NeedPatchDto>(needId);
  const priorityLabel = useNeedPriorityLabel();
  const { selectedOrg } = useLoggedInWithOrgContextState();

  return (
    <Box maxW="container.lg" mx="auto" pb="10">
      <Button
        size="sm"
        variant="ghost"
        leftIcon={<Icon as={FiArrowLeft} />}
        color="smMuted"
        onClick={() => navigate(urls.v2.needs.mine)}
        mb="3"
      >
        {t("Back to my needs")}
      </Button>

      <Flex justify="space-between" align="flex-start" gap="4" mb="6" wrap="wrap">
        <Box>
          <Heading as="h1" size="lg" color="smText" mb="2">
            {state.title?.trim() || t("Untitled")}
          </Heading>
          <HStack spacing="3" color="smMuted" fontSize="sm">
            <NeedStatusTag status={state.status} />
            <Text>
              {t("Reported by")} {personName(state.createdBy)} · {formatDate(state.createdAt)}
            </Text>
          </HStack>
        </Box>
        {state.canEdit && (
          <Button
            colorScheme="teal"
            variant="outline"
            leftIcon={<Icon as={FiEdit} />}
            onClick={() => navigate(urls.v2.needs.edit.go(state.id))}
          >
            {t("Edit")}
          </Button>
        )}
      </Flex>

      <Stack spacing="5">
        <Box bg="smBackground" border="1px solid" borderColor="smBorder" rounded="xl" p="6">
          <NeedStatusStepper status={state.status} />
        </Box>

        <NeedActions needId={needId} />

        <SectionCard icon={FiFileText} title={t("Description")}>
          {!isTiptapContentEmpty(state.description) ? (
            <TipTap content={state.description ?? ""} editable={false} hasBorder={false} padding="0" />
          ) : (
            <Text color="smMuted" fontSize="sm">
              {DASH}
            </Text>
          )}
        </SectionCard>

        <SectionCard icon={FiDollarSign} title={t("Value")}>
          <Stack spacing="5">
            <LabelValueGrid
              rows={[
                { label: t("Estimated value"), value: formatValue(state.estimatedValue?.value) },
                { label: t("Budget line"), value: state.budgetLine || DASH },
              ]}
            />
            {!isTiptapContentEmpty(state.valueAssessment) && (
              <Stack spacing="1">
                <Text color="smMuted" fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
                  {t("Initial value assessment")}
                </Text>
                <TipTap content={state.valueAssessment ?? ""} editable={false} hasBorder={false} padding="0" />
              </Stack>
            )}
          </Stack>
        </SectionCard>

        <SectionCard icon={FiBriefcase} title={t("Procurement")}>
          <LabelValueGrid
            rows={[
              ...(organizationHasSeveralDepartments(selectedOrg)
                ? [{ label: t("Department"), value: state.department?.name ?? DASH }]
                : []),
              { label: t("Priority"), value: state.priority ? priorityLabel(state.priority) : DASH },
              {
                label: t("What does the procurement concern?"),
                value: state.procurementType ? t(state.procurementType) : DASH,
              },
              { label: t("Desired delivery date"), value: formatLocalDate(state.deliveryDate) },
            ]}
          />
        </SectionCard>

        <SectionCard icon={FiUser} title={t("Contact")}>
          <LabelValueGrid
            rows={[
              { label: t("Contact person"), value: state.contactPerson ? personName(state.contactPerson) : DASH },
              { label: t("Reported by"), value: personName(state.createdBy) },
              { label: t("Created"), value: formatDate(state.createdAt) },
            ]}
          />
        </SectionCard>

        <SectionCard icon={FiFile} title={t("Attachments")} count={state.documents?.length}>
          {(state.documents?.length ?? 0) === 0 ? (
            <Text color="smMuted" fontSize="sm">
              {t("No attachments")}
            </Text>
          ) : (
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>{t("Name")}</Th>
                    <Th isNumeric>{t("Size")}</Th>
                    <Th>{t("Uploaded by")}</Th>
                    <Th>{t("Uploaded at")}</Th>
                    <Th />
                  </Tr>
                </Thead>
                <Tbody>
                  {(state.documents ?? []).map((doc) => (
                    <Tr key={doc.id}>
                      <Td color="smText">{doc.name}</Td>
                      <Td color="smMuted" isNumeric>
                        {formatFileSize(doc.size)}
                      </Td>
                      <Td color="smMuted">{personName(doc.uploadedBy)}</Td>
                      <Td color="smMuted">{formatDate(doc.uploadedAt)}</Td>
                      <Td isNumeric>
                        <DownloadDocumentIconButton documentId={doc.id} size="sm" />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </SectionCard>
      </Stack>
    </Box>
  );
};

const personName = (p: { firstName?: string | null; lastName?: string | null }): string => {
  const name = [p.firstName, p.lastName].filter(Boolean).join(" ").trim();
  return name || DASH;
};

const formatValue = (value: number | null | undefined): string =>
  value == null ? DASH : `${nokFormatter.format(value)} NOK`;

const formatDate = (instant: string): string => {
  const d = new Date(instant);
  return Number.isNaN(d.getTime())
    ? instant
    : `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};
const formatLocalDate = (yyyyMmDd: string | null | undefined): string => {
  if (!yyyyMmDd) return DASH;
  const parts = yyyyMmDd.split("-");
  return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : yyyyMmDd;
};
const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
